import { z } from 'zod';
import { SPORT_IDS } from '../sports/index';
import { activityScoresSchema } from './scoring';

const sportIdEnum = z.enum(SPORT_IDS as [string, ...string[]]);

export const createActivitySchema = z.object({
  sportType: sportIdEnum,
  format: z.enum(['pickup', 'league_match', 'tournament', 'training']).default('pickup'),
  // nullable: the mobile client sends `null` for empty optional fields
  // (title.trim() || null, venueId ?? null) — without .nullable() the
  // backend 400s on creation from mobile. Web sends `undefined`; both ok now.
  title: z.string().max(200).nullable().optional(),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  time: z.string().regex(/^\d{2}:\d{2}$/),
  durationMinutes: z.number().int().min(5).max(480).default(120),
  venueId: z.string().uuid().nullable().optional(),
  locationText: z.string().max(500).nullable().optional(),
  /** Precise coordinates for a free-text place picked from the autocomplete
   *  (no venue record). When present the backend uses them verbatim for the map
   *  marker; when absent it geocodes `locationText` within the city instead. */
  lat: z.number().min(-90).max(90).nullable().optional(),
  lng: z.number().min(-180).max(180).nullable().optional(),
  teamMode: z.enum(['none', '2_teams', 'n_teams', 'pairs', 'individual']).optional(),
  scoringMode: z.enum(['none', 'team_score', 'sets', 'individual_time']).optional(),
  maxParticipants: z.number().int().min(2).max(100).nullable().optional(),
  hasParticipantLimit: z.boolean().optional(),
  signupMode: z.enum(['open', 'invite', 'priority', 'geo_open']).default('open'),
  isPublic: z.boolean().default(false),
  hasAnnouncement: z.boolean().default(false),
  announcementTime: z.string().regex(/^\d{2}:\d{2}$/).optional(),
  isRated: z.boolean().optional().default(true),
  /** When true, the creator is auto-enrolled as a player right after create —
   *  open games join immediately; announced (draft) games cast the creator's
   *  "yes" vote so they're counted when signup opens. Default false preserves
   *  the old behaviour for any client that doesn't send the flag. */
  joinAsPlayer: z.boolean().optional().default(false),
});

export const updateActivitySchema = createActivitySchema.partial();

export const submitScoreSchema = z.object({
  scores: activityScoresSchema,
  notes: z.string().max(1000).optional(),
});
